import React, { Fragment, useEffect, useRef, useState, useCallback } from "react";
import "./event.css";
import Cta from "../components/cta";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ticketicon from "../assets/ticketicon.svg";
import removeIcon from "../assets/remove.png";
import { useLocation } from "react-router-dom";
import FreaksPromo from "../components/FreaksPromo";
import { useLanguage } from '../contexts/LanguageContext';
import AffiliateCards from '../components/cards';
import FreakyardCards from "../components/freakyard-cards";
import PdfViewer from "../components/PdfViewer";

const BASE_PERKS = [
  "Access to all stages",
  "Entertainment Zone",
  "Art exhibition",
];

const BASE_INCLUDES = ["Feature", "Feature", "Feature", "Feature"];

const WEEKEND_OPTIONS = [
  {
    idSuffix: "weekend1",
    label: "Weekend 1",
    dayLabel: "Thursday",
    dateLabel: "Jan 30",
  },
  {
    idSuffix: "weekend2",
    label: "Weekend 2",
    dayLabel: "Friday",
    dateLabel: "Jan 31",
  },
];

const DAY_OPTIONS = [
  {
    idSuffix: "day1",
    label: "Day 1",
    dayLabel: "Thursday",
    dateLabel: "Jan 30",
  },
  { idSuffix: "day2", label: "Day 2", dayLabel: "Friday", dateLabel: "Jan 31" },
];

const buildOptions = (prefix, sourceOptions) =>
  sourceOptions.map((option) => ({
    id: `${prefix}-${option.idSuffix}`,
    label: option.label,
    dayLabel: option.dayLabel,
    dateLabel: option.dateLabel,
    summaryLabel: `${option.label} • ${option.dayLabel} ${option.dateLabel}`,
    price: option.price,
  }));

const TICKET_TABS = [
  {
    id: "tab1",
    label: "Multi-day Pass",
    tickets: [
      {
        key: "multi-general",
        title: "General Admission",
        price: 250,
        compareAt: 299,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("multi-general", WEEKEND_OPTIONS),
      },
      {
        key: "multi-golden",
        title: "Golden Circle",
        price: 420,
        compareAt: 499,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("multi-golden", WEEKEND_OPTIONS),
      },
      {
        key: "multi-vip",
        title: "VIP",
        price: 690,
        compareAt: 759,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("multi-vip", WEEKEND_OPTIONS),
      },
      {
        key: "multi-tables",
        title: "Tables",
        price: 1850,
        compareAt: 1990,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("multi-tables", WEEKEND_OPTIONS),
      },
      {
        key: "multi-boxes",
        title: "Boxes",
        price: 2350,
        compareAt: 2490,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("multi-boxes", WEEKEND_OPTIONS),
      },
    ],
  },
  {
    id: "tab2",
    label: "Day Pass",
    tickets: [
      {
        key: "day-general",
        title: "General Admission",
        price: 180,
        compareAt: 210,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("day-general", DAY_OPTIONS),
      },
      {
        key: "day-golden",
        title: "Golden Circle",
        price: 320,
        compareAt: 360,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("day-golden", DAY_OPTIONS),
      },
      {
        key: "day-vip",
        title: "VIP",
        price: 520,
        compareAt: 580,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("day-vip", DAY_OPTIONS),
      },
      {
        key: "day-tables",
        title: "Tables",
        price: 950,
        compareAt: 1020,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("day-tables", DAY_OPTIONS),
      },
      {
        key: "day-boxes",
        title: "Boxes",
        price: 1200,
        compareAt: 1320,
        perks: BASE_PERKS,
        includes: BASE_INCLUDES,
        options: buildOptions("day-boxes", DAY_OPTIONS),
      },
    ],
  },
];

const TICKET_OPTIONS = TICKET_TABS.flatMap((tab) =>
  tab.tickets.flatMap((ticket) =>
    ticket.options.map((option) => ({
      ...option,
      ticketKey: ticket.key,
      ticketTitle: ticket.title,
      ticketPrice: option.price ?? ticket.price,
      ticketCompareAt: ticket.compareAt,
      tabId: tab.id,
      tabLabel: tab.label,
    }))
  )
);

const INITIAL_COUNTS = TICKET_OPTIONS.reduce((acc, option) => {
  acc[option.id] = 0;
  return acc;
}, {});

const roundToTwo = (value) => Math.round(value * 100) / 100;

const formatCurrency = (value) =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatSar = (value) => `${formatCurrency(value)} SAR`;
const FAQ_DATA = {
  en: [
    {
      id: "faq-what-is",
      question: "What is Freakyard?",
      answer: "Freakyard 2026 is where we turn an industrial yard into a living world filled with music, color, and human energy. It's the moment the Freaks arrive — and everything lights up. If you've been with us before, you know the feeling. If this is your first time, trust us… you'll get it the second you walk in!",
    },
    {
      id: "faq-dates",
      question: "What are the dates?",
      answer: "Weekend 1: February 5-6, 2026\nWeekend 2: February 12-13, 2026\n\nEvent time is from 6 PM until 4 AM (each night)\nLast entry: 2:30 AM\n\nCome early — the first hours always hit different.",
    },
    {
      id: "faq-location",
      question: "Where is it happening?",
      answer: "At the MDL Beast Soundstorm Site in Banban, Riyadh. Easy to reach, easy to navigate.\nXGW2+VP, Banban, Riyadh 13571, Saudi Arabia",
    },
    {
      id: "faq-parking",
      question: "Is there parking?",
      answer: "Yes — on-site parking for all ticket tiers.",
    },
    {
      id: "faq-ride-hailing",
      question: "Can I use ride-hailing apps?",
      answer: "Definitely! There are dedicated areas for drop-off and pick-up.",
    },
    {
      id: "faq-age",
      question: "Who can attend?",
      answer: "The festival is 18+. Bring your ID and you're good. No ID, no entry – no exceptions. Minors will not be permitted, even if accompanied by an adult.",
    },
    {
      id: "faq-tickets-buy",
      question: "Where do I buy tickets?",
      answer: "Right on the Freaks of Nature website and NOFOMO channels and resellers. NOFOMO is our main ticketing partner - they handle payments, access, and transfers securely.",
    },
    {
      id: "faq-ticket-prices",
      question: "How do ticket prices work?",
      answer: "Ticket prices are released in phases, starting with Early Bird. Early Bird ends on December 31st, so that's the moment to grab the best price before the next phase opens. All prices and phases will always be up-to-date on our ticketing page — simple, clear, and always official.",
    },
    {
      id: "faq-refund-transfer",
      question: "Are tickets refundable or transferable?",
      answer: "• Tickets are non-refundable.\n• You can transfer your ticket safely through the NOFOMO app. It takes a few taps, and it's instant.",
    },
    {
      id: "faq-ticket-support",
      question: "I'm having an issue with my ticket — who helps me?",
      answer: "NOFOMO support is here for you:\n\nWhatsApp: +966 55 406 7794\nSat–Thu: 10 AM – 2 AM\nFri: 3 PM – 1 AM\n\nEmail: contact@nofomo.com\n\nHotline: +966 9200 00208\n\nThey'll sort it out quickly.",
    },
    {
      id: "faq-vip",
      question: "I want VIP/VVIP bookings.",
      answer: "If you're looking for a more elevated experience — better views, smoother access, or a private space for your group — our VIP and VVIP options make the night feel extra special. For tables, boxes, or lounge bookings, reach out to: vip@disruptgroup.me\n\nWe'll help you design a night that feels effortless, comfortable, and memorable.",
    },
    {
      id: "faq-lineup",
      question: "When will you announce the lineup?",
      answer: "Our lineup is coming out in two phases this year — we like building the excitement little by little. Keep an eye on our social channels… that's where everything drops first. And trust us, this lineup is worth the wait. 🎧✨",
    },
    {
      id: "faq-timetable",
      question: "When will the timetable be released?",
      answer: "You'll find the full timetable on the Freaks of Nature festival app about two weeks before the festival. It's the easiest way to plan your night, follow your favorite artists, and stay updated. Make sure you download the app — it's basically your guide to the yard.",
    },
    {
      id: "faq-stages",
      question: "What stages can I expect?",
      answer: "This edition is built around three worlds — each with its own vibe:\n\nMainstage: Where the big storytelling happens. Layers of sound, visuals, and SFX coming together for a cinematic feel.\n\nUnderground Stage: Immersive, deep, and tech-driven. Expect new AV tricks, darker grooves, and that lost in the moment energy.\n\nHouse of Freaks: You're stepping into the Freak Squad's home — house music, personality, easiness, and a bit of playful madness.",
    },
    {
      id: "faq-music",
      question: "What kind of music will be played?",
      answer: "Progressive house, big room, techno, house, Afro house, tech house — plus fresh sounds entering the Saudi scene for the first time.",
    },
    {
      id: "faq-food",
      question: "Can I bring food or drinks?",
      answer: "No. Outside food and beverages are not permitted. But don't worry… the yard will have plenty of good options available for purchase.",
    },
    {
      id: "faq-merch",
      question: "Will there be merch?",
      answer: "Yes — exclusive drops from Freak by Freaks of Nature. Limited quantities, unique pieces.",
    },
    {
      id: "faq-camera",
      question: "Can I bring my camera?",
      answer: "Yes — phones, small cameras, and normal cameras are allowed.",
    },
    {
      id: "faq-filming",
      question: "Will the festival be filmed?",
      answer: "Yes — the festival will be captured for recap and content. By entering, you're agreeing to be part of the documentation.",
    },
    {
      id: "faq-lost-found",
      question: "Is there a Lost & Found?",
      answer: "Yes — they'll be a dedicated area on-site. If you misplace something, visit the team there and they'll help you track it down.",
    },
    {
      id: "faq-accessibility",
      question: "Is the festival accessible?",
      answer: "Yes. We have:\n• Accessible routes\n• Accessible restrooms\n• Support teams on-site\n• Viewing areas (where needed)\n\nWe want everyone to feel welcome and comfortable.",
    },
    {
      id: "faq-dj-apply",
      question: "I want to DJ at Freaks of Nature — how do I apply?",
      answer: "We love seeing new talent rise — and we always keep an eye out for the next Freak behind the decks. Every edition, we host a DJ competition where anyone with passion and a solid mix can jump in and take their shot. The moment the competition opens, we announce it on our social channels. So stay close, stay tuned… and when the time comes, bring your sound, your style, and your freakness. We can't wait to hear what you've got. ⚡🎶",
    },
    {
      id: "faq-artist-showcase",
      question: "I'm a visual artist or fashion designer and want to showcase my work.",
      answer: "We're always excited to discover new creatives — especially the ones who bring color, expression, and bold ideas into the world. If you feel like your work fits the Freaks spirit, we'd genuinely love to see it. Send your portfolio to: hello@freaksofnature.me\n\nOur commercial team will reach out, walk you through the process, and explore the possibilities with you. Whether you're into visuals, fashion, or something wonderfully weird — this is your place to shine. ✨",
    },
    {
      id: "faq-partnership",
      question: "I want to collaborate or partner with the festival.",
      answer: "We're all about meaningful partnerships — the kind where creativity feels natural and the energy feels right. If you're looking to build something together, something your brand and our freaks community can both feel proud of, reach out to: marketing@disruptgroup.me\n\nLet's create moments, stories, and experiences that stand out.",
    },
    {
      id: "faq-space-rental",
      question: "I want to rent a space or host an activation.",
      answer: "Got an idea you want to bring to life inside the yard? We love activations that add value, spark curiosity, or make people smile. Whether it's an installation, a brand moment, or something totally new, email: commercial@freaksofnature.me\n\nOur team will guide you through the options and help you shape the experience in a way that fits naturally inside Freakyard.",
    },
  ],
  ar: [
    {
      id: "faq-what-is",
      question: "ما هو فريكيارد؟",
      answer: "فريكيارد ٢٠٢٦ هو حدث نحول فيه ساحة صناعية إلى عالم حي مليء بالموسيقى والألوان والطاقة. إنها اللحظة التي يجتمع فيها الفريكس ليضيئوا الأجواء. إذا سبق وزرت فعاليات فريكس أوف نيتشر، ستتعرّف على الشعور فورًا. وإذا كانت هذه زيارتك الأولى… ستفهم كل شيء بمجرد دخولك الساحة."
    },
    {
      id: "faq-dates",
      question: "ما هي المواعيد؟",
      answer: "الويك اند الأول:\n٥–٦ فبراير ٢٠٢٦\nالويك اند الثاني:\n١٢–١٣ فبراير ٢٠٢٦\n\nتبدأ الفعاليات من الساعة ٦ مساءً حتى ٤ صباحًا يوميًا.\nآخر موعد للدخول: الساعة ٢:٣٠ صباحًا.\n\nننصح بالحضور مبكرًا — بداية الليلة لها طعم مختلف."
    },
    {
      id: "faq-location",
      question: "أين سيقام المهرجان؟",
      answer: "في موقع ساوندستورم التابع لـ MDLBEAST في بنبان، الرياض.\n\nسهل الوصول وسهل في التنقّل.\n\nالعنوان: XGW2+VP، بنبان، الرياض ١٣٥٧١، المملكة العربية السعودية."
    },
    {
      id: "faq-parking",
      question: "هل تتوفر مواقف سيارات؟",
      answer: "نعم، تتوفر مواقف داخل الموقع لجميع فئات التذاكر."
    },
    {
      id: "faq-ride-hailing",
      question: "هل يمكن استخدام تطبيقات النقل؟",
      answer: "بالتأكيد! توجد مناطق مخصّصة للنزول والالتقاط."
    },
    {
      id: "faq-age",
      question: "من يستطيع الحضور؟",
      answer: "الدخول متاح لمن هم بعمر ١٨ عامًا فما فوق.\nيجب إحضار الهوية. بدون هوية، لا يُسمح بالدخول دون استثناءات.\n\nلن يسمح بدخول من هم دون الـ ١٨ عامًا، حتى مع وجود مرافق بالغ."
    },
    {
      id: "faq-tickets-buy",
      question: "أين يمكن شراء التذاكر؟",
      answer: "من موقع فريكس أوف نيتشر ومن قنوات NOFOMO الرسمية.\nمنصة NOFOMO هي شريك التذاكر الرئيسي، وتدير عمليات الدفع والدخول والتحويل بشكل آمن."
    },
    {
      id: "faq-ticket-prices",
      question: "كيف تُحدَد أسعار التذاكر؟",
      answer: "تُطرح التذاكر على مراحل، تبدأ بمرحلة الشراء المبكر (Early Bird).\n\nتنتهي مرحلة Early Bird في ٣١ ديسمبر — وهي أفضل فرصة للحصول على أقل سعر قبل فتح المرحلة التالية.\nستجد أسعار المراحل محدثة دائمًا في صفحة التذاكر."
    },
    {
      id: "faq-refund-transfer",
      question: "هل التذاكر قابلة للاسترجاع أو التحويل؟",
      answer: "• التذاكر غير قابلة للاسترجاع.\n• يمكن تحويل التذكرة بأمان عبر تطبيق NOFOMO بخطوات بسيطة وفورية."
    },
    {
      id: "faq-ticket-support",
      question: "أواجه مشكلة في تذكرتي — من يساعدني؟",
      answer: "دعم NOFOMO متوفر عبر:\n\nواتساب (WhatsApp)\nالسبت – الخميس: ١٠ صباحًا – ٢ صباحًا\nالجمعة: ٣ مساءً – ١ صباحًا\n‎966‎ 55‎ 406‎ 7794+\n\nالبريد الإلكتروني\ncontact@nofomo.com\n\nالخط الساخن\n‎966‎ 9200‎ 00208+\n\nفريق الدعم يتعامل مع جميع الطلبات بسرعة وكفاءة."
    },
    {
      id: "faq-vip",
      question: "أرغب في حجز VIP أو VVIP.",
      answer: "إذا كنت تبحث عن تجربة أكثر تميزًا — رؤية أفضل، دخول أسهل، أو مساحة خاصة — فإن خيارات VIP و VVIP لدينا ستجعل ليلتك استثنائية.\n\nلحجز الطاولات، الصناديق الخاصة (Boxes)، أو لاونچ، يمكنك التواصل معنا عبر:\nvip@disruptgroup.me\n\nسيساعدك الفريق في اختيار تجربة مريحة، سهلة، ولا تُنسى."
    },
    {
      id: "faq-lineup",
      question: "متى سيتم الإعلان عن ال لاين اب؟",
      answer: "سيتم الإعلان على مرحلتين هذا العام. نحن نحب أن نبني الحماس تدريجيًا.\n\nتابعوا صفحاتنا على السوشيال ميديا، لأن جميع الإعلانات ستُطلق هناك في البداية.\nونؤكد لكم، هذا اللاين أب يستحق الانتظار. 🎧✨"
    },
    {
      id: "faq-timetable",
      question: "متى سيصدر الجدول؟",
      answer: "الجدول الكامل سيكون متاحًا في تطبيق مهرجان فريكس أوف نيتشر (Freaks of Nature festival app) قبل الفعالية بحوالي أسبوعين.\nالتطبيق هو أسهل طريقة لتخطيط ليلتك، متابعة فنّانيك المفضّلين، والبقاء على اطلاع بآخر التحديثات.\nاحرص على تحميل التطبيق — فهو دليلك الرسمي داخل الساحة."
    },
    {
      id: "faq-stages",
      question: "ما هي مسارح المهرجان؟",
      answer: "يضم هذا الإصدار ثلاثة عوالم مختلفة:\n\nMainstage\n(المسرح الرئيسي)\n\nتجربة بصرية وصوتية متكاملة، مؤثرات خاصة، وتفاصيل سينمائية.\n\nUnderground Stage\n(مسرح الأندرغراوند)\n\nأجواء عميقة، تقنية، إيقاعات مظلمة، وتجارب سمعية بصرية(AV) جديدة، وشعور بالانغماس في كل لحظة.\n\nHouse of Freaks\n(بيت الفريكس)\n\nهناك تدخل منزل الفريك سكواد. مساحة ذات طابع مرح، مريحة، وقريبة من روح شخصيات الـ\"فريك سكواد\"."
    },
    {
      id: "faq-music",
      question: "ما هي أنواع الموسيقى في المهرجان؟",
      answer: "Progressive House، Big Room، Techno، House، Afro House، Tech House، بالإضافة إلى أصوات جديدة تُعرض لأول مرة في الساحة السعودية."
    },
    {
      id: "faq-food",
      question: "هل يمكن إحضار طعام أو مشروبات؟",
      answer: "لا. لا يُسمح بإدخال الطعام أو المشروبات من خارج الساحة.\nلكن لا تقلقوا، ستتوفر خيارات متنوعة داخل الساحة للشراء."
    },
    {
      id: "faq-merch",
      question: "هل سيكون هناك منتجات للبيع (merch)؟",
      answer: "نعم — منتجات حصرية من Freak by Freaks of Nature. قطع فريدة بكميات محدودة."
    },
    {
      id: "faq-camera",
      question: "هل يمكنني جلب كاميرا؟",
      answer: "نعم — يُسمح بالهواتف والكاميرات الشخصية الصغيرة (غير الاحترافية)."
    },
    {
      id: "faq-filming",
      question: "هل سيتم تصوير الحدث؟",
      answer: "نعم، سيتم التصوير لأغراض التوثيق.\nدخولك يعني موافقتك على الظهور في المحتوى."
    },
    {
      id: "faq-lost-found",
      question: "هل يوجد قسم للمفقودات؟",
      answer: "نعم — سيكون هناك منطقة مخصصة للمفقودات داخل الموقع.\nإذا أضعت أي شيء، يرجى زيارة الفريق هناك وسيساعدونك في تتبع واستعادة أغراضك."
    },
    {
      id: "faq-accessibility",
      question: "هل الفعالية متاحة لذوي الإعاقة؟",
      answer: "نعم. قمنا بتوفير ما يلي:\n\nمسارات مخصّصة ومُهيّأة\nدورات مياه مخصّصة (Accessible restrooms)\nفرق دعم متواجدة في الموقع\nمناطق مشاهدة مخصّصة (عند الحاجة)\n\nهدفنا هو أن يشعر الجميع بالترحيب والراحة التامة."
    },
    {
      id: "faq-dj-apply",
      question: "أريد المشاركة كـ دي چاي — كيف أتقدم بطلب؟",
      answer: "نحن نحب رؤية صعود المواهب الجديدة، ونتطلع دائمًا لاكتشاف الفريك القادم خلف \"الدي جي بوث\".\n\nفي كل إصدار، نستضيف مسابقة للدي جيز، حيث يمكن لأي شخص يمتلك الشغف وmix قوي أن يشارك ويغتنم الفرصة.\nبمجرد فتح باب التسجيل للمسابقة، سنعلن ذلك فورًا على قنواتنا الاجتماعية.\n\nلذا، ترقبوا واستعدوا... إذا كنت مهتمًا، جهّز الـ mix الخاص بك وتابعنا. ⚡🎶"
    },
    {
      id: "faq-artist-showcase",
      question: "أنا فنان بصري أو مصمم أزياء وأرغب بعرض أعمالي.",
      answer: "نسعد دائمًا باكتشاف المبدعين الجدد — خاصة الذين يجلبون الألوان والتعبير والأفكار الجريئة إلى العالم.\nإذا كنت تشعر بأن عملك يتناسب مع روح الفريكس، فنحن نتطلع إلى رؤيته.\nأرسل ملف أعمالك إلى:\n hello@freaksofnature.me\n\nسيتواصل معك فريقنا التجاري لمناقشة التفاصيل وإطلاعك على الخطوات اللازمة واستكشاف الفرص المتاحة معك.\nسواء كنت متخصصًا في الفنون البصرية، الأزياء، أو شيء غريب ورائع ولم يُسمع به من قبل، فهذا هو مكانك للتألق. ✨"
    },
    {
      id: "faq-partnership",
      question: "أرغب بالتعاون أو الشراكة مع المهرجان.",
      answer: "نحن نؤمن بالشراكات ذات المغزى والتي يكون فيها الإبداع عفويًا والطاقة مناسبة.\nإذا كنت تتطلع إلى بناء شيء معنا، شيء يفخر به كل من علامتك التجارية ومجتمع الفريكس، تواصل مع: marketing@disruptgroup.me\n\nدعنا نخلق معًا لحظات وقصص وتجارب لا تُنسى."
    },
    {
      id: "faq-space-rental",
      question: "أرغب باستئجار مساحة أو تنفيذ اكتيفيشن.",
      answer: "نحن نحب الـ اكتيفيشن التي تضيف قيمة، تثير الفضول، أو ترسم البسمة على وجوه الحضور.\n\nسواء كانت تركيبًا فنيًا، فعالية لعلامة تجارية، أو شيئًا مبتكرًا بالكامل، تواصل معنا عبر البريد الإلكتروني: commercial@freaksofnature.me\n\nسيساعدك فريقنا في دراسة الخيارات المتاحة وصياغة التجربة بطريقة تتلاءم مع فريكيارد."
    }
  ]
};


function Event() {
  const { isArabic } = useLanguage();

  const [activeTab, setActiveTab] = useState(TICKET_TABS[0].id);
  const [activeTicket, setActiveTicket] = useState(null);
  const [counts, setCounts] = useState(() => ({ ...INITIAL_COUNTS }));
  const [isStickyOpen, setIsStickyOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const location = useLocation();
  const pendingHashRef = useRef(null);

  const activeTabData =
    TICKET_TABS.find((tab) => tab.id === activeTab) ?? TICKET_TABS[0];

  const currentFaqs = isArabic ? FAQ_DATA.ar : FAQ_DATA.en;

  const translations = {
    days: isArabic ? 'يوم' : 'Days',
    hours: isArabic ? 'ساعة' : 'Hours',
    minutes: isArabic ? 'دقيقة' : 'Minutes',
    dateRange: isArabic ? 'فبراير ٥-٦ و ١٢-١٣' : 'FEB 5-6 & 12-13',
    stages: isArabic ? '٣ مسارح' : '3 Stages',
    djs: isArabic ? '١٠٠+ دي جي' : '100+ DJs',
    joinNextEvent: isArabic ? 'احصل على تذاكرك الآن' : 'Get your Tickets Now',
    // joinNextEvent: isArabic ? 'انضم إلى الحدث القادم' : 'Join Next Event',

    followUs: isArabic ? 'تابعنا' : 'Follow Us',
    embraceFreakness: isArabic ? 'عِش طاقتك الفريكية بأسلوبك الخاص' : 'Embrace Your Freakness in Style',
    comingSoon: isArabic ? 'قريبًا' : 'Coming Soon',
    notifyMe: isArabic ? 'أرسلوا لي إشعارًا' : 'Notify Me',
    downloadApp: isArabic ? 'حمّل تطبيقنا' : 'Download Our App',
    stepIntoFreakShow: isArabic ? 'ادخل عالم ال Freak Show' : 'Step into the Freak Show',
    affiliateText: isArabic
      ? 'تابع أقوى السيتات واللحظات الأسطورية عبر منصة ‎Relive‎، وتعرّف على مجتمعنا الإبداعي الجريء، وانضم كأفلييت لنشر التجربة وكسب مكافآت رهيبة.'
      : 'Catch up on iconic sets and unforgettable moments through our Relive platform, dive deep into our wild, creative community, and spread the word as an affiliate to earn epic rewards.'
  };
  const selectedOptions = TICKET_OPTIONS.filter(
    (option) => (counts[option.id] ?? 0) > 0
  ).map((option) => {
    const count = counts[option.id] ?? 0;
    return {
      ...option,
      count,
      totalPrice: roundToTwo(count * option.ticketPrice),
    };
  });

  const subtotal = roundToTwo(
    selectedOptions.reduce((sum, item) => sum + item.totalPrice, 0)
  );
  const discount = subtotal ? roundToTwo(subtotal * 0.1) : 0;
  const subtotalAfterDiscount = subtotal ? roundToTwo(subtotal - discount) : 0;
  const fees = subtotal ? roundToTwo(subtotalAfterDiscount * 0.06) : 0;
  const totalDue = subtotal ? roundToTwo(subtotalAfterDiscount + fees) : 0;
  const totalQuantity = selectedOptions.reduce(
    (sum, item) => sum + item.count,
    0
  );
  const hasSelections = selectedOptions.length > 0;
  const discountLabel = discount > 0 ? `-${formatSar(discount)}` : formatSar(0);

  // Helper function to scroll to element with proper header offset
  const scrollToElement = useCallback((elementId) => {
    const element = document.getElementById(elementId);
    if (!element) {
      return false;
    }

    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Get fresh element reference in case DOM updated
        const freshElement = document.getElementById(elementId);
        if (!freshElement) return;

        // Get element position
        const elementRect = freshElement.getBoundingClientRect();
        // Get absolute position from top of document
        const absoluteTop = elementRect.top + window.scrollY;

        // Header offset based on screen size (accounting for countdown banner on mobile)
        // Desktop: 124px header
        // Mobile (≤880px): 124px header + countdown banner offset (54px) = 178px total
        // Extra small (≤468px): 124px header + countdown banner offset (40px) = 164px total
        const getHeaderOffset = () => {
          const width = window.innerWidth;
          if (width <= 468) {
            return 164; // 124px header + 40px banner offset
          } else if (width <= 880) {
            return 178; // 124px header + 54px banner offset
          } else {
            return 124; // Just header height on desktop
          }
        };
        const headerOffset = getHeaderOffset();

        // Position element at top of viewport with header offset (not centered)
        // This ensures the element is fully visible below the header
        const scrollPosition = Math.max(0, absoluteTop - headerOffset);

        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      });
    });

    return true;
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setActiveTicket(null);
  };

  const toggleTicketAccordion = (ticketKey) => {
    setActiveTicket((prev) => (prev === ticketKey ? null : ticketKey));
  };

  const handleIncrement = (optionId) => {
    setCounts((prev) => ({
      ...prev,
      [optionId]: (prev[optionId] ?? 0) + 1,
    }));
  };

  const handleDecrement = (optionId) => {
    setCounts((prev) => {
      const current = prev[optionId] ?? 0;
      if (current <= 0) {
        return prev;
      }
      return {
        ...prev,
        [optionId]: current - 1,
      };
    });
  };

  const handleRemoveItem = (optionId) => {
    setCounts((prev) => {
      if (!prev[optionId]) {
        return prev;
      }
      return {
        ...prev,
        [optionId]: 0,
      };
    });
  };

  const handleClearAll = () => {
    if (!hasSelections) {
      return;
    }
    setCounts(() => ({ ...INITIAL_COUNTS }));
  };

  const toggleStickyOrder = () => {
    setIsStickyOpen((prev) => !prev);
  };

  const toggleFaqAccordion = (faqId) => {
    setActiveFaq((prev) => (prev === faqId ? null : faqId));
  };

  // Helper function to process hash
  const processHash = useCallback((hashValue) => {
    if (!hashValue) {
      return;
    }

    const normalizedHash = hashValue.toLowerCase();

    // Set the appropriate tab based on the hash
    if (normalizedHash === "day-pass" || normalizedHash === "tickets-day-pass") {
      setActiveTab("tab2");
      // Scroll to tickets section which contains the day-pass tab
      pendingHashRef.current = "tickets";
    } else if (
      normalizedHash === "multi-day-pass" ||
      normalizedHash === "weekly-pass" ||
      normalizedHash === "tickets-multi-day-pass"
    ) {
      setActiveTab("tab1");
      // Scroll to tickets section which contains the multi-day-pass tab
      pendingHashRef.current = "tickets";
    } else if (normalizedHash === "tickets") {
      //eta huinea 
      if (activeTab === null || activeTab === undefined) {
        setActiveTab("tab1");
      }
      pendingHashRef.current = "tickets";
    } else if (normalizedHash === "dj-comp" || normalizedHash === "affiliate") {
      pendingHashRef.current = "djcomp";
    } else if (normalizedHash === "faq") {
      pendingHashRef.current = "faq";
    } else {
      pendingHashRef.current = hashValue;
    }
  }, []);

  // Handle hash on mount and when pathname changes (for navigation from other pages)
  useEffect(() => {
    // When navigating from another page, wait a bit longer for DOM to be ready
    const timeoutId = setTimeout(() => {
      // Check window.location.hash first (more reliable when navigating from another page)
      const windowHash = window.location.hash.replace("#", "");
      if (windowHash) {
        processHash(windowHash);
        return;
      }

      // Fallback to location.hash from React Router
      const locationHash = location.hash.replace("#", "");
      if (locationHash) {
        processHash(locationHash);
      }
    }, 50); // Small delay to ensure DOM is ready

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.hash, processHash]);

  // Handle hash changes from location (for same-page navigation)
  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const hashValue = location.hash.replace("#", "");
    if (!hashValue) {
      return;
    }

    processHash(hashValue);
  }, [location.hash, processHash]);

  // Listen for native hashchange events (for when clicking links on same page)
  useEffect(() => {
    const handleHashChange = () => {
      const hashValue = window.location.hash.replace("#", "");
      if (!hashValue) {
        return;
      }

      processHash(hashValue);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [processHash]);

  useEffect(() => {
    if (!pendingHashRef.current) {
      return;
    }

    const scrollTarget = pendingHashRef.current;

    // Clear the pending ref immediately to prevent duplicate scrolls
    pendingHashRef.current = null;

    // Use a longer delay when navigating from another page to ensure DOM is fully ready
    // Check if we just navigated from another page by checking if we're at the top
    const isNavigatingFromOtherPage = window.scrollY === 0 || window.pageYOffset === 0;
    const delay = isNavigatingFromOtherPage ? 400 : 100;

    const timeoutId = window.setTimeout(() => {
      // Double-check element exists before scrolling
      const element = document.getElementById(scrollTarget);
      if (element) {
        scrollToElement(scrollTarget);
      } else {
        // If element still doesn't exist, try one more time after a longer delay
        setTimeout(() => {
          const retryElement = document.getElementById(scrollTarget);
          if (retryElement) {
            scrollToElement(scrollTarget);
          }
        }, 200);
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [scrollToElement, location.pathname]);

  return (
    <div>
      <section className="hero-event"> </section>
      <section id="tickets">
        <FreaksPromo />
        <PdfViewer />
      </section>
      {/* <section className="event-section" id="tickets">
        <div className="event-div">
          <div className="event-tabs">
            <div className="tabs">
              {TICKET_TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={activeTab === tab.id ? "tab active" : "tab"}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div
              className="tab-content"
              id={activeTab === "tab1" ? "multi-day-pass" : "day-pass"}
            >
              {activeTabData.tickets.map((ticket) => {
                const accordionKey = `${activeTabData.id}-${ticket.key}`;
                const isActive = activeTicket === accordionKey;
                return (
                  <div
                    key={ticket.key}
                    className={`accordin-item ${isActive ? "active" : ""}`}
                  >
                    <div
                      className="accordin-header"
                      onClick={() => toggleTicketAccordion(accordionKey)}
                    >
                      <div className="accordin-main-title">
                        <h3>{ticket.title}</h3>
                        <h4>
                          SAR {formatCurrency(ticket.price)}
                          {ticket.compareAt && (
                            <>
                              {" "}
                              <span className="line-through">
                                SAR {formatCurrency(ticket.compareAt)}
                              </span>
                            </>
                          )}
                        </h4>
                      </div>
                      <span className={`arrow ${isActive ? "rotate" : ""}`}>
                        <FaChevronDown className="arrow-icon-down" />
                      </span>
                    </div>
                    {isActive && (
                      <div className="accordin-content">
                        <div className="accordin-content-div">
                          <div className="content-text">
                            <h3>
                              {ticket.perks.map((perk, index) => (
                                <Fragment key={perk}>
                                  {perk}
                                  {index < ticket.perks.length - 1 && <br />}
                                </Fragment>
                              ))}
                            </h3>
                          </div>
                          <div className="content-ticket">
                            <h4>Ticket include:</h4>
                            <div className="ticket-list">
                              {ticket.includes.map((include) => (
                                <p key={include}>{include}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="accordin-content-div1">
                          {ticket.options.map((option) => {
                            const optionCount = counts[option.id] ?? 0;
                            return (
                              <div className="weekend-div" key={option.id}>
                                <h3>{option.label}</h3>
                                {optionCount > 0 ? (
                                  <div className="plusminus-function">
                                    <div className="function-text">
                                      {option.dayLabel}{" "}
                                      <span>{option.dateLabel}</span>
                                    </div>
                                    <div className="function-plusminus">
                                      <div
                                        className="plusminus1 plus"
                                        onClick={() =>
                                          handleIncrement(option.id)
                                        }
                                        role="button"
                                      >
                                        +
                                      </div>
                                      <div className="plusminus1 number">
                                        {optionCount}
                                      </div>
                                      <div
                                        className="plusminus1 minus"
                                        onClick={() =>
                                          handleDecrement(option.id)
                                        }
                                        role="button"
                                      >
                                        -
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="add-function"
                                    onClick={() => handleIncrement(option.id)}
                                    role="button"
                                  >
                                    <div className="function-text">
                                      {option.dayLabel}{" "}
                                      <span>{option.dateLabel}</span>
                                    </div>
                                    <div className="add-cta">+ Add</div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="event-order">
            <div className="event-order-div order-summary-card">
              <div className="order-summary-header">
                <h4>ORDER SUMMARY</h4>
                <button
                  type="button"
                  className="clear-button desktop"
                  onClick={handleClearAll}
                  disabled={!hasSelections}
                >
                  Clear all
                </button>
              </div>

              <div className="order-summary-body">
                {hasSelections ? (
                  selectedOptions.map((item) => (
                    <div className="order-summary-item" key={item.id}>
                      <div className="order-summary-copy">
                        <span className="order-summary-qty">{item.count}x</span>
                        <div>
                          <p className="order-summary-title">
                            {item.ticketTitle}
                          </p>
                          <p className="order-summary-detail">
                            {item.summaryLabel}
                          </p>
                        </div>
                      </div>
                      <div className="order-summary-meta">
                        <span className="order-summary-price">
                          {formatSar(item.totalPrice)}
                        </span>
                        <button
                          type="button"
                          className="remove-button desktop"
                          onClick={() => handleRemoveItem(item.id)}
                          aria-label={`Remove ${item.ticketTitle}`}
                        >
                          <img src={removeIcon} alt="Remove item" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="order-summary-empty">
                    Select tickets to see them appear here.
                  </p>
                )}
              </div>

              <div className="order-summary-totals">
                <p>
                  <span>Total</span>
                  <span>{formatSar(subtotal)}</span>
                </p>
                <p>
                  <span>Discount (10%)</span>
                  <span>{discountLabel}</span>
                </p>
                <p>
                  <span>Ticketing fees (6%)</span>
                  <span>{formatSar(fees)}</span>
                </p>
                <p className="order-summary-total-due">
                  <span>Total Amount Due</span>
                  <span>{formatSar(totalDue)}</span>
                </p>
              </div>

              <button
                type="button"
                className="total-button"
                disabled={!hasSelections}
              >
                CONFIRM
              </button>

              <p className="order-summary-note">
                *All Tickets are nonrefundable *Name in the ticket must match
                name in tawaklna * Full Ticket T&C <span>HERE</span>
              </p>
            </div>
          </div>
        </div>
      </section> */}
      <section id="affiliate">
        <section className="affiliate-section py-5 text-center">
          <div className="affiliate-div">
            {/* <h2>{translations.stepIntoFreakShow}</h2>
          <p className='para'>{translations.affiliateText}</p> */}

            <FreakyardCards />

          </div>
        </section>      </section>

      <section className="FAQs-section" id="faq">
        <div className="FAQs-div">
          <h2>{isArabic ? 'الأسئلة الشائعة' : 'FAQs'}</h2>

          <div className="FAQs-accordion">
            {currentFaqs.map((faq) => {
              const isActive = activeFaq === faq.id;
              return (
                <div className="FAQs-accordion-item" key={faq.id}>
                  <div
                    className="FAQs-accordion-header"
                    onClick={() => toggleFaqAccordion(faq.id)}
                  >
                    <span className="plusminus">{isActive ? "-" : "+"}</span>
                    <span>{faq.question}</span>
                  </div>
                  {isActive && (
                    <div className="FAQs-accordion-content">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* <section className={`sticky-order ${isStickyOpen ? "open" : ""}`}>
        <div
          className={`sticky-arrow ${isStickyOpen ? "down" : ""}`}
          onClick={toggleStickyOrder}
        >
          <FaChevronUp className="rotate-arrow" />
        </div>

        {isStickyOpen ? (
          <div className="sticky-order-summary">
            <div className="summary">
              <div className="box-summary1">
                <span className="summary-clear-cta">
                  <h4>Summary Order</h4>
                  <button
                    type="button"
                    className="clear-button"
                    onClick={handleClearAll}
                    disabled={!hasSelections}
                  >
                    clear all
                  </button>
                </span>

                <div className="summary-items">
                  {hasSelections ? (
                    selectedOptions.map((item) => (
                      <div className="s-box-card" key={item.id}>
                        <div className="s-card-text">
                          <div className="s-card-copy">
                            <p className="s-card-title">
                              <span>{item.count}x</span> {item.ticketTitle}
                            </p>
                            <p className="s-card-detail">{item.summaryLabel}</p>
                          </div>
                          <div className="s-card-meta">
                            <span className="s-card-price">
                              {formatSar(item.totalPrice)}
                            </span>
                            <button
                              type="button"
                              className="remove-button"
                              onClick={() => handleRemoveItem(item.id)}
                              aria-label={`Remove ${item.ticketTitle}`}
                            >
                              <img src={removeIcon} alt="Remove item" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="order-summary-empty">Your order is empty.</p>
                  )}
                </div>
              </div>

              <div className="box-summary2">
                <div className="total-summary">
                  <p>
                    Total <span>{formatSar(subtotal)}</span>
                  </p>
                  <p>
                    Discount (10%) <span>{discountLabel}</span>
                  </p>
                  <p>
                    Ticketing fees (6%) <span>{formatSar(fees)}</span>
                  </p>
                  <p>
                    Total Amount Due <span>{formatSar(totalDue)}</span>
                  </p>
                </div>
                <div className="final-total">
                  <p>
                    Total: <span>{formatSar(totalDue)}</span>
                  </p>
                </div>

                <button
                  type="button"
                  className="total-button"
                  disabled={!hasSelections}
                >
                  CONFIRM
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="sticky-order-content">
            <div className="ticket-icon">
              <p>
                <img
                  src={ticketicon}
                  alt="Freaks of Nature"
                  className="img-fluid"
                />{" "}
                x{totalQuantity}
              </p>
              <h4>Total: {formatSar(totalDue)}</h4>
            </div>

            <button
              type="button"
              className="confirm-button"
              disabled={!hasSelections}
            >
              CONFIRM
            </button>
          </div>
        )}
      </section> */}
    </div>
  );
}

export default Event;
